import React, { useMemo, useState } from 'react';
import {
  IconVariable,
  IconUser,
  IconApi,
  IconChevronDown,
  IconChevronRight,
  IconSparkles,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import './VariablesPanel.css';

const SOURCE_LABEL = {
  extract: 'IA',
  set_variable: 'Nó',
  collect_field: 'Cliente',
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'verdadeiro' : 'falso';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value || '—';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const truncate = (str, max = 200) => {
  if (typeof str !== 'string') return str;
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

const VariableRow = ({ name, value, type, source, isChanged, isMissing }) => (
  <div className={`vp-row ${isMissing ? 'vp-row-missing' : ''}`}>
    <div className="vp-row-header">
      <span className="vp-row-name">{name}</span>
      {type && <span className="vp-row-type">{type}</span>}
      {isChanged && !isMissing && <span className="vp-badge vp-badge-new">novo</span>}
      {isMissing && <span className="vp-badge vp-badge-pending">aguardando</span>}
    </div>
    <div className="vp-row-value">
      {isMissing ? <span className="vp-value-missing">—</span> : formatValue(value)}
    </div>
    {source && <div className="vp-row-source">{source}</div>}
  </div>
);

const ApiResponseRow = ({ name, value }) => {
  const [expanded, setExpanded] = useState(false);
  let formatted = '';
  try {
    formatted = JSON.stringify(value, null, 2);
  } catch {
    formatted = String(value);
  }
  const isLong = formatted.length > 200;
  return (
    <div className="vp-row">
      <div className="vp-row-header">
        <span className="vp-row-name">{name}</span>
      </div>
      <pre className="vp-api-value">
        {expanded || !isLong ? formatted : truncate(formatted, 200)}
      </pre>
      {isLong && (
        <button
          type="button"
          className="vp-expand-btn"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Recolher' : 'Expandir'}
        </button>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, count, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  // Esconder seções vazias para reduzir ruído.
  if (count === 0) return null;
  return (
    <div className="vp-section">
      <button
        type="button"
        className="vp-section-header"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        <Icon size={15} className="vp-section-icon" />
        <span className="vp-section-title">{title}</span>
        <span className="vp-section-count">{count}</span>
      </button>
      {open && <div className="vp-section-body">{children}</div>}
    </div>
  );
};

/**
 * VariablesPanel — drawer overlay mostrando as variáveis declaradas
 * pelo usuário no canvas e seus valores em tempo real.
 *
 * Mostra APENAS variáveis declaradas no graph_definition
 * (extract_variables, set_variable, collect_field). Variáveis de runtime
 * (ex: user_response do save_response_as) ficam escondidas — não são
 * "criadas pelo usuário", são técnicas.
 *
 * Props:
 *   isOpen, onClose       — controle externo do drawer
 *   variables             — state.variables do agente (lookup de valores)
 *   collectedData         — state.collected_data (lookup de valores)
 *   apiResponses          — debug-only, mostra preview JSON
 *   declaredVariables     — esqueleto vindo do graph_definition
 *   recentlyChangedKeys   — Set de chaves que mudaram no último turno
 *   onReset               — callback para limpar acumulado (botão ↻)
 */
const VariablesPanel = ({
  isOpen = false,
  onClose,
  variables = {},
  collectedData = {},
  apiResponses = {},
  declaredVariables = [],
  recentlyChangedKeys = new Set(),
  onReset,
}) => {
  // Constrói linhas APENAS para variáveis declaradas no canvas.
  // Variáveis runtime sem declaração são ignoradas propositalmente.
  const { extractRows, collectRows } = useMemo(() => {
    const extract = [];
    const collect = [];

    declaredVariables.forEach((d) => {
      const fromExtract = d.source === 'extract' || d.source === 'set_variable';
      const valueSource = fromExtract ? variables : collectedData;
      const hasValue = d.name in valueSource;
      const row = {
        name: d.name,
        type: d.type,
        value: hasValue ? valueSource[d.name] : undefined,
        source: `${SOURCE_LABEL[d.source] || d.source}: ${d.source_node_label}`,
        isChanged: recentlyChangedKeys.has(d.name),
        isMissing: !hasValue,
      };
      if (fromExtract) {
        extract.push(row);
      } else {
        collect.push(row);
      }
    });

    return { extractRows: extract, collectRows: collect };
  }, [variables, collectedData, declaredVariables, recentlyChangedKeys]);

  const apiResponseEntries = useMemo(
    () => Object.entries(apiResponses || {}),
    [apiResponses]
  );

  const totalDeclared = extractRows.length + collectRows.length;
  const populatedCount = extractRows.filter((r) => !r.isMissing).length
    + collectRows.filter((r) => !r.isMissing).length;

  return (
    <div className={`variables-drawer ${isOpen ? 'variables-drawer--open' : ''}`}>
      <div className="vp-header">
        <h6 className="vp-title">
          <IconSparkles size={16} className="me-2" />
          Variáveis do Agente
          {totalDeclared > 0 && (
            <span className="vp-title-meta">
              {populatedCount}/{totalDeclared} preenchidas
            </span>
          )}
        </h6>
        <div className="vp-header-actions">
          {onReset && totalDeclared > 0 && (
            <button
              type="button"
              className="vp-icon-btn"
              onClick={onReset}
              title="Limpar valores acumulados"
            >
              <IconRefresh size={14} />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="vp-icon-btn"
              onClick={onClose}
              title="Fechar painel"
            >
              <IconX size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="vp-body">
        {totalDeclared === 0 && apiResponseEntries.length === 0 ? (
          <div className="vp-empty">
            <IconVariable size={32} className="vp-empty-icon" />
            <div className="vp-empty-title">Nenhuma variável declarada</div>
            <div className="vp-empty-text">
              Adicione variáveis em <strong>Variáveis a Extrair</strong> nos nós de IA
              do canvas para que apareçam aqui durante a conversa.
            </div>
          </div>
        ) : (
          <>
            <Section
              icon={IconVariable}
              title="Variáveis Extraídas"
              count={extractRows.length}
            >
              {extractRows.map((row) => (
                <VariableRow key={row.name} {...row} />
              ))}
            </Section>

            <Section
              icon={IconUser}
              title="Dados Coletados"
              count={collectRows.length}
            >
              {collectRows.map((row) => (
                <VariableRow key={row.name} {...row} />
              ))}
            </Section>

            <Section
              icon={IconApi}
              title="Respostas de API"
              count={apiResponseEntries.length}
              defaultOpen={false}
            >
              {apiResponseEntries.map(([name, value]) => (
                <ApiResponseRow key={name} name={name} value={value} />
              ))}
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

export default VariablesPanel;
